/**
 * Application Class Selector Component
 * 
 * Allows users to select which application classes (target systems)
 * should be used for AC generation in a project.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Store,
  Settings,
  Smartphone,
  Users,
  Code,
  Wrench,
  Check,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Info,
  Loader2,
  AlertCircle,
  Save,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface ApplicationClass {
  id: string;
  name: string;
  description: string | null;
  personas: string[];
  acTemplates: string[];
  icon: string | null;
  popularity: number;
  isSystem: boolean;
  isEnabled?: boolean;
  customConfig?: {
    customPersonas?: string[];
    customAcTemplates?: string[];
  };
  sortOrder?: number;
}

interface Props {
  projectId: string;
  onSave?: () => void;
}

// ============================================
// Icon Mapping
// ============================================

const ICON_MAP: Record<string, React.ReactNode> = {
  storefront: <Store className="w-5 h-5" />,
  admin_panel: <Settings className="w-5 h-5" />,
  mobile: <Smartphone className="w-5 h-5" />,
  partner: <Users className="w-5 h-5" />,
  api: <Code className="w-5 h-5" />,
  internal: <Wrench className="w-5 h-5" />,
};

const getIcon = (iconId: string | null) => {
  if (!iconId) return <Settings className="w-5 h-5" />;
  return ICON_MAP[iconId] || <Settings className="w-5 h-5" />;
};

// ============================================
// Component
// ============================================

export function ApplicationClassSelector({ projectId, onSave }: Props) {
  const queryClient = useQueryClient();
  const [selectedClasses, setSelectedClasses] = useState<Set<string>>(new Set());
  const [expandedClass, setExpandedClass] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch project's application classes
  const { data: classesData, isLoading, error } = useQuery<ApplicationClass[]>({
    queryKey: ['project-application-classes', projectId],
    queryFn: async () => {
      const response = await api.get<{ data: ApplicationClass[] }>(`/application-classes/project/${projectId}`);
      return response.data;
    },
  });

  // Initialize selected classes from fetched data
  useEffect(() => {
    if (classesData) {
      const enabled = new Set(
        classesData.filter(c => c.isEnabled).map(c => c.id)
      );
      setSelectedClasses(enabled);
      setHasChanges(false);
    }
  }, [classesData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      return api.put(`/application-classes/project/${projectId}`, {
        enabledClasses: Array.from(selectedClasses),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-application-classes', projectId] });
      setHasChanges(false);
      onSave?.();
    },
  });

  // Toggle class selection
  const toggleClass = (classId: string) => {
    setSelectedClasses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(classId)) {
        newSet.delete(classId);
      } else {
        newSet.add(classId);
      }
      return newSet;
    });
    setHasChanges(true);
  };

  // Toggle expanded state for details
  const toggleExpanded = (classId: string) => {
    setExpandedClass(prev => prev === classId ? null : classId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <span className="ml-2 text-neutral-600">Loading application classes...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-error-50 border border-error-200 rounded-lg">
        <div className="flex items-center gap-2 text-error-700">
          <AlertCircle className="w-5 h-5" />
          <span>Failed to load application classes</span>
        </div>
      </div>
    );
  }

  const classes = classesData || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">
            Target Systems
          </h3>
          <p className="text-sm text-neutral-600 mt-1">
            Select which systems should receive generated acceptance criteria
          </p>
        </div>
        
        {hasChanges && (
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="btn btn-primary flex items-center gap-2"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Changes
          </button>
        )}
      </div>

      {/* Info Banner */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Multi-System AC Generation</p>
          <p className="mt-1">
            When you generate acceptance criteria, Specuno will create separate AC sections 
            for each selected system with appropriate personas and scenarios.
          </p>
        </div>
      </div>

      {/* Class List */}
      <div className="space-y-2">
        {classes.map((appClass) => {
          const isSelected = selectedClasses.has(appClass.id);
          const isExpanded = expandedClass === appClass.id;

          return (
            <div
              key={appClass.id}
              className={`
                border rounded-lg transition-all duration-200
                ${isSelected 
                  ? 'border-primary bg-primary-50/50' 
                  : 'border-neutral-200 bg-white hover:border-neutral-300'
                }
              `}
            >
              {/* Main Row */}
              <div className="flex items-center p-4 gap-4">
                {/* Drag Handle (for future reordering) */}
                <div className="text-neutral-400 cursor-grab">
                  <GripVertical className="w-4 h-4" />
                </div>

                {/* Checkbox */}
                <button
                  onClick={() => toggleClass(appClass.id)}
                  className={`
                    w-5 h-5 rounded border-2 flex items-center justify-center
                    transition-colors duration-150
                    ${isSelected 
                      ? 'bg-primary border-primary text-white' 
                      : 'border-neutral-300 hover:border-primary'
                    }
                  `}
                >
                  {isSelected && <Check className="w-3 h-3" />}
                </button>

                {/* Icon */}
                <div className={`
                  w-10 h-10 rounded-lg flex items-center justify-center
                  ${isSelected ? 'bg-primary-100 text-primary' : 'bg-neutral-100 text-neutral-600'}
                `}>
                  {getIcon(appClass.icon)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-neutral-900">
                      {appClass.name}
                    </h4>
                    {appClass.isSystem && (
                      <span className="px-1.5 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-600 rounded">
                        System
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-neutral-600 truncate">
                    {appClass.description}
                  </p>
                </div>

                {/* Personas Count */}
                <div className="text-right">
                  <div className="text-sm font-medium text-neutral-900">
                    {appClass.personas.length} personas
                  </div>
                  <div className="text-xs text-neutral-500">
                    {appClass.acTemplates.length} AC types
                  </div>
                </div>

                {/* Expand Button */}
                <button
                  onClick={() => toggleExpanded(appClass.id)}
                  className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-neutral-600" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-neutral-600" />
                  )}
                </button>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-2 border-t border-neutral-100">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Personas */}
                    <div>
                      <h5 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2">
                        Personas
                      </h5>
                      <div className="flex flex-wrap gap-1.5">
                        {appClass.personas.map((persona) => (
                          <span
                            key={persona}
                            className="px-2 py-1 text-xs bg-neutral-100 text-neutral-700 rounded-full"
                          >
                            {persona.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* AC Templates */}
                    <div>
                      <h5 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2">
                        AC Types
                      </h5>
                      <div className="flex flex-wrap gap-1.5">
                        {appClass.acTemplates.slice(0, 6).map((template) => (
                          <span
                            key={template}
                            className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-full"
                          >
                            {template.replace(/_/g, ' ')}
                          </span>
                        ))}
                        {appClass.acTemplates.length > 6 && (
                          <span className="px-2 py-1 text-xs bg-neutral-100 text-neutral-500 rounded-full">
                            +{appClass.acTemplates.length - 6} more
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected Summary */}
      {selectedClasses.size > 0 && (
        <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-neutral-700">
                {selectedClasses.size} system{selectedClasses.size !== 1 ? 's' : ''} selected
              </span>
              <p className="text-xs text-neutral-500 mt-0.5">
                AC will be generated for: {Array.from(selectedClasses).join(', ')}
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedClasses(new Set());
                setHasChanges(true);
              }}
              className="text-sm text-neutral-600 hover:text-neutral-900"
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* Save Error */}
      {saveMutation.isError && (
        <div className="p-3 bg-error-50 border border-error-200 rounded-lg flex items-center gap-2 text-error-700">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">Failed to save changes. Please try again.</span>
        </div>
      )}

      {/* Success Message */}
      {saveMutation.isSuccess && !hasChanges && (
        <div className="p-3 bg-success-50 border border-success-200 rounded-lg flex items-center gap-2 text-success-700">
          <Check className="w-4 h-4" />
          <span className="text-sm">Changes saved successfully!</span>
        </div>
      )}
    </div>
  );
}

export default ApplicationClassSelector;
